import { IPipelineDashboardFilters } from '@/bundles/Pipeline/types';
import { IPipelineDashboardFiltersDto } from '@/bundles/Pipeline/dto';
import { PipelineMeasureEnum } from '@/bundles/Pipeline/types/enums/PipelineMeasureEnum';
import { PipelineKanbanSortEnum } from '@/bundles/Pipeline/types/enums/PipelineKanbanSortEnum';
import { PipelineStageEnum } from '@/bundles/Pipelines/types';
import {
  IQueryRecord,
  IQueryValue,
  enumToQuery,
  queryToEnum,
  toBoolean,
  numberArraysEqual,
  toNumberArray,
} from '@/bundles/DashboardAnalytics/helpers/dashboardFiltersQuery';
import {
  BASE_FILTERS_QUERY_KEYS,
  baseFiltersEqual,
  baseFiltersToQuery,
  baseQueryHasFilters,
  baseQueryToFilters,
  dealsPipelineFiltersToDto,
} from '@/bundles/DashboardAnalytics/helpers/dealsPipelineFiltersQuery';
import { PIPELINE_STAGE_ORDER } from '@/bundles/Pipeline/helpers/dashboard/pipelineColumns';

export { viewToQuery, queryToView } from '@/bundles/DashboardAnalytics/helpers/dashboardFiltersQuery';

export const PIPELINE_FILTERS_QUERY_KEYS = {
  ...BASE_FILTERS_QUERY_KEYS,
  stages: 'stage',
};

function toStageArray (value: IQueryValue): PipelineStageEnum[] {
  const valid = PIPELINE_STAGE_ORDER as number[];
  const stages = toNumberArray(value).filter((item): item is PipelineStageEnum => valid.includes(item));
  return Array.from(new Set(stages));
}

export function filtersToQuery (filters: IPipelineDashboardFilters): Record<string, string> {
  const query = baseFiltersToQuery(filters);
  if (filters.stages.length) {
    query[PIPELINE_FILTERS_QUERY_KEYS.stages] = filters.stages.join(',');
  }
  return query;
}

export function queryToFilters (query: IQueryRecord): IPipelineDashboardFilters {
  return {
    ...baseQueryToFilters(query),
    stages: toStageArray(query[PIPELINE_FILTERS_QUERY_KEYS.stages]),
  };
}

export function queryHasFilters (query: IQueryRecord): boolean {
  return baseQueryHasFilters(query, [PIPELINE_FILTERS_QUERY_KEYS.stages]);
}

export function filtersEqual (a: IPipelineDashboardFilters, b: IPipelineDashboardFilters): boolean {
  return baseFiltersEqual(a, b) && numberArraysEqual(a.stages, b.stages);
}

export function filtersToDto (filters: IPipelineDashboardFilters): IPipelineDashboardFiltersDto {
  const dto: IPipelineDashboardFiltersDto = dealsPipelineFiltersToDto(filters);
  if (filters.stages.length) {
    dto.stage = [...filters.stages];
  }
  return dto;
}

export function measureToQuery (measure: PipelineMeasureEnum): Record<string, string> {
  return enumToQuery(measure, 'measure', PipelineMeasureEnum.agentGross);
}

export function queryToMeasure (query: IQueryRecord): PipelineMeasureEnum {
  return queryToEnum(query, 'measure', Object.values(PipelineMeasureEnum), PipelineMeasureEnum.agentGross);
}

export function sortToQuery (sort: PipelineKanbanSortEnum): Record<string, string> {
  return enumToQuery(sort, 'sort', PipelineKanbanSortEnum.consideration);
}

export function queryToSort (query: IQueryRecord): PipelineKanbanSortEnum {
  return queryToEnum(query, 'sort', Object.values(PipelineKanbanSortEnum), PipelineKanbanSortEnum.consideration);
}

export const VISUALIZATIONS_QUERY_KEY = 'visualizations';

// Only the open section is written to the query — closed is the default and stays out of the URL.
export function visualizationsToQuery (open: boolean): Record<string, string> {
  return open ? { [VISUALIZATIONS_QUERY_KEY]: 'true' } : {};
}

export function queryToVisualizations (query: IQueryRecord): boolean {
  return toBoolean(query[VISUALIZATIONS_QUERY_KEY]);
}

export function queryHasYear (query: IQueryRecord): boolean {
  return toNumberArray(query[PIPELINE_FILTERS_QUERY_KEYS.year]).length > 0;
}

export function getCurrentYearRange (): number[] {
  return [new Date().getFullYear()];
}

export function getThreeYearForecastRange (): number[] {
  const [year] = getCurrentYearRange();
  return [year, year + 1, year + 2];
}

export function isThreeYearForecastRange (years: number[]): boolean {
  const range = getThreeYearForecastRange();
  return years.length === range.length && range.every(year => years.includes(year));
}
